import { Sun, Moon, Layers } from 'lucide-react';
import { Button } from './ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator
} from './ui/dropdown-menu';
import { Slider } from './ui/slider';
import { useTheme } from '../context/ThemeContext';
import { SearchBar } from './SearchBar';
import { cn } from '../lib/utils';
import { BASE_MAPS } from '../lib/baseMaps';

// compact: Handy-Layout (auch Tablet hochkant und Handy quer) – niedriger, kleineres Logo
export const Header = ({ onLocationSelect, mapBrightness, onBrightnessChange, baseMap, onBaseMapChange, compact = false }) => {
  const { toggleTheme, isDark } = useTheme();

  return (
    <header
      className={cn(
        "shrink-0 border-b border-border bg-card/95 backdrop-blur flex items-center justify-between z-50 pt-[env(safe-area-inset-top)]",
        compact
          ? "h-[calc(3.5rem+env(safe-area-inset-top))] pl-[max(0.5rem,env(safe-area-inset-left))] pr-[max(0.5rem,env(safe-area-inset-right))]"
          : "h-[calc(4rem+env(safe-area-inset-top))] px-4"
      )}
      data-testid="header"
    >
      {/* Left Section */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-3">
          {/* Lünefire Logo */}
          <img 
            src="/lunefire-icon.svg"
            alt="Lünefire"
            className={compact ? "w-8 h-8" : "w-10 h-10"}
          />
          <div className="hidden sm:block">
            <h1 className="font-display text-xl font-bold tracking-tight leading-none">
              <span className="text-[#DC2626]">Lüne</span>
              <span className="text-foreground">fire</span>
              <span className="text-muted-foreground text-sm font-normal ml-2">FPS</span>
            </h1>
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest">
              Feuerwehr Planspiel System
            </p>
          </div>
        </div>
      </div>

      {/* Center Section - Search */}
      <div className={cn("flex-1 min-w-0 max-w-md", compact ? "mx-2" : "mx-4")}>
        <SearchBar onLocationSelect={onLocationSelect} />
      </div>

      {/* Right Section */}
      <div className={cn("flex items-center", compact ? "gap-1" : "gap-2")}>
        {/* Kartenstil und Helligkeit */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="rounded-md"
              aria-label="Karte"
              data-testid="map-settings-btn"
            >
              <Layers className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64 p-3">
            <DropdownMenuLabel className="text-xs uppercase tracking-wider font-display">
              Kartenstil
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup value={baseMap} onValueChange={onBaseMapChange}>
              {Object.entries(BASE_MAPS).map(([key, { label }]) => (
                <DropdownMenuRadioItem key={key} value={key} className="touch:h-10" data-testid={`base-map-${key}`}>
                  {label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator className="my-2" />
            <DropdownMenuLabel className="text-xs uppercase tracking-wider font-display">
              Kartenhelligkeit
            </DropdownMenuLabel>
            <div className="flex items-center gap-3 mt-2">
              <Moon className="w-4 h-4 text-muted-foreground" />
              <Slider
                value={[mapBrightness]}
                onValueChange={(value) => onBrightnessChange(value[0])}
                min={30}
                max={150}
                step={5}
                className="flex-1"
                data-testid="brightness-slider"
              />
              <Sun className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground text-center mt-2">{mapBrightness}%</p>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="rounded-md"
          data-testid="theme-toggle-btn"
        >
          {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </Button>

      </div>
    </header>
  );
};

export default Header;
